// Shopify semi-automated onboarding — super-admin approval queue.
//
// Lists ShopifyAccessRequest rows across all tenants and lets a
// super-admin approve/reject a pending request with one click.
// Approving flips the row to 'ready' and Slack-pings the operator
// channel; the brand's UI then shows "Install Shopify" and OAuth
// proceeds on their click.
//
// Workflow for the operator:
//   1. See the row in this list (also notified via Slack on create)
//   2. Click the "Open Partner Dashboard" link → add the shop to the
//      Custom Distribution allowed-stores list in the Shopify UI
//   3. Click "Approve" here — brand gets unblocked
//
// Backend 403s non-super-admins; this page gates render to avoid a
// deep-link spinner.

import { useCallback, useEffect, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Card, CardBody, Code, HStack, Link,
  Skeleton, Text, VStack, useToast,
} from '@chakra-ui/react';
import { PageHeader } from '../../shell/PageHeader';
import { apiJson, isApiError } from '../../auth/apiFetch';
import { useAuth } from '../../auth/AuthContext';

type RequestStatus = 'pending' | 'ready' | 'installed' | 'rejected';

type ShopifyRequest = {
  _id:            string;
  shopDomain:     string;
  status:         RequestStatus;
  brand:          { _id: string; name: string | null } | null;
  requesterEmail: string | null;
  createdAt:      string;
  approvedAt:     string | null;
  rejectedAt:     string | null;
  installedAt:    string | null;
  notes:          string | null;
};

type ListResponse = {
  requests: ShopifyRequest[];
  partnerDashboardLink: string;
};

const STATUS_COLOR: Record<RequestStatus, string> = {
  pending:   'blue',
  ready:     'green',
  installed: 'purple',
  rejected:  'gray',
};

const REFRESH_MS = 15000;

export function AdminShopifyRequestsPage() {
  const auth = useAuth();
  const toast = useToast();
  const jwtIsAdmin = auth.status === 'authenticated' && auth.user.isSuperAdmin === true;

  const [requests, setRequests] = useState<ShopifyRequest[] | null>(null);
  const [partnerLink, setPartnerLink] = useState<string>('https://partners.shopify.com/current/apps');
  const [loading, setLoading] = useState(jwtIsAdmin);
  const [forbidden, setForbidden] = useState(!jwtIsAdmin);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filter, setFilter] = useState<RequestStatus | 'all'>('pending');

  const load = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (!jwtIsAdmin) return;
    if (!opts.silent) setLoading(true);
    try {
      const qs = filter === 'all' ? 'all' : filter;
      const r = await apiJson<ListResponse>(`/api/admin/shopify-requests?status=${qs}&limit=100`);
      setRequests(r.requests);
      setPartnerLink(r.partnerDashboardLink);
      setError(null);
      setForbidden(false);
    } catch (err: unknown) {
      if (isApiError(err) && err.status === 403) {
        setForbidden(true);
        setRequests(null);
      } else {
        setError(err instanceof Error ? err.message : 'load failed');
      }
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, [jwtIsAdmin, filter]);

  useEffect(() => {
    load();
    const t = window.setInterval(() => load({ silent: true }), REFRESH_MS);
    return () => window.clearInterval(t);
  }, [load]);

  const approve = useCallback(async (id: string) => {
    setBusyId(id);
    try {
      await apiJson<{ ok: boolean; status: RequestStatus }>(
        `/api/admin/shopify-requests/${id}/approve`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      toast({ title: 'Approved — brand notified', status: 'success', duration: 3000 });
      await load({ silent: true });
    } catch (err: unknown) {
      toast({
        title: 'Approve failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error', duration: 5000,
      });
    } finally {
      setBusyId(null);
    }
  }, [toast, load]);

  const reject = useCallback(async (id: string) => {
    const notes = window.prompt('Reason to show the brand (optional):') || null;
    setBusyId(id);
    try {
      await apiJson<{ ok: boolean }>(
        `/api/admin/shopify-requests/${id}/reject`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ notes }),
        }
      );
      toast({ title: 'Rejected', status: 'info', duration: 3000 });
      await load({ silent: true });
    } catch (err: unknown) {
      toast({
        title: 'Reject failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error', duration: 5000,
      });
    } finally {
      setBusyId(null);
    }
  }, [toast, load]);

  if (forbidden) {
    return (
      <Box>
        <PageHeader title="Shopify access requests" description="Approve a merchant's shop so they can complete OAuth. Added to Partner Dashboard allowlist → click Approve here → brand's UI unblocks." />
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            This page is restricted to super-admins. If you need access to this
            surface, contact a super-admin.
          </Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title="Shopify access requests" description="Approve a merchant's shop so they can complete OAuth. Added to Partner Dashboard allowlist → click Approve here → brand's UI unblocks." />
      <VStack align="stretch" spacing={4}>
        <Card variant="outline" bg="purple.50" borderColor="purple.200">
          <CardBody>
            <VStack align="flex-start" spacing={2}>
              <Text fontSize="sm" color="purple.900">
                <strong>Approval workflow:</strong> for each pending request,
                open the Partner Dashboard, add the shop to the Custom
                Distribution allowed-stores list, then click <strong>Approve</strong>{' '}
                here. The brand's UI auto-polls and will show an{' '}
                <strong>Install</strong> button within 5 seconds.
              </Text>
              <Link href={partnerLink} isExternal color="purple.700" fontWeight="600" fontSize="sm">
                Open Shopify Partner Dashboard →
              </Link>
            </VStack>
          </CardBody>
        </Card>

        <HStack spacing={2}>
          {(['pending', 'ready', 'installed', 'rejected', 'all'] as const).map((s) => (
            <Button
              key={s}
              size="xs"
              variant={filter === s ? 'solid' : 'outline'}
              colorScheme={filter === s ? 'purple' : 'gray'}
              onClick={() => setFilter(s)}
            >
              {s}
            </Button>
          ))}
        </HStack>

        {loading && !requests && (
          <VStack align="stretch" spacing={2}>
            <Skeleton height="48px" />
            <Skeleton height="48px" />
            <Skeleton height="48px" />
          </VStack>
        )}

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">{error}</Text>
          </Alert>
        )}

        {requests && requests.length === 0 && (
          <Card variant="outline">
            <CardBody>
              <Text fontSize="sm" color="brand.muted" textAlign="center">
                No {filter === 'all' ? '' : filter} requests.
              </Text>
            </CardBody>
          </Card>
        )}

        {requests && requests.length > 0 && (
          <VStack align="stretch" spacing={2}>
            {requests.map((r) => (
              <Card key={r._id} variant="outline">
                <CardBody>
                  <HStack justify="space-between" align="flex-start" spacing={3}>
                    <VStack align="flex-start" spacing={1} flex="1" minW={0}>
                      <HStack spacing={2}>
                        <Badge colorScheme={STATUS_COLOR[r.status]}>{r.status}</Badge>
                        <Text fontSize="sm" fontWeight="600">
                          {r.brand?.name || `brand ${r.brand?._id?.slice(-6) || '?'}`}
                        </Text>
                      </HStack>
                      <HStack spacing={2} fontSize="xs" color="brand.muted" flexWrap="wrap">
                        <Code fontSize="xs">{r.shopDomain}</Code>
                        {r.requesterEmail && <Text>· {r.requesterEmail}</Text>}
                        <Text>· submitted {new Date(r.createdAt).toLocaleString()}</Text>
                      </HStack>
                      {r.notes && (
                        <Text fontSize="xs" color="brand.muted" fontStyle="italic">
                          Note: {r.notes}
                        </Text>
                      )}
                    </VStack>
                    <HStack spacing={2}>
                      {r.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            colorScheme="green"
                            onClick={() => approve(r._id)}
                            isLoading={busyId === r._id}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            colorScheme="red"
                            onClick={() => reject(r._id)}
                            isDisabled={busyId === r._id}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      {r.status === 'ready' && (
                        <Text fontSize="xs" color="brand.muted">
                          Waiting on brand install
                        </Text>
                      )}
                      {r.status === 'installed' && r.installedAt && (
                        <Text fontSize="xs" color="brand.muted">
                          Installed {new Date(r.installedAt).toLocaleString()}
                        </Text>
                      )}
                    </HStack>
                  </HStack>
                </CardBody>
              </Card>
            ))}
          </VStack>
        )}
      </VStack>
    </Box>
  );
}

export default AdminShopifyRequestsPage;
